import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useOS } from '@/lib/os-context';
import { getGlassStyle, isGlassSupported } from '@/lib/glass-theme';
import { liquidPanel } from '@/lib/liquid-motion';

interface CalendarPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CalendarPopup({ isOpen, onClose }: CalendarPopupProps) {
  const { displaySettings, effectiveColorMode } = useOS();
  const [currentDate, setCurrentDate] = useState(new Date());
  const today = new Date();

  // Glass theme support
  const isTahoe = displaySettings.theme === 'tahoe';
  const glassEnabled = isTahoe && isGlassSupported();
  const glassStyles = glassEnabled 
    ? getGlassStyle(displaySettings.glassIntensity, displaySettings.glassVariant, effectiveColorMode)
    : {};

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOfMonth = getFirstDayOfMonth(currentDate);

  const isToday = (day: number | null) => {
    if (day === null) return false;
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  // Generate calendar days array
  const calendarDays = [];
  
  // Empty cells for days before month starts
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-9998"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Calendar Panel */}
          <motion.div
            className={cn(
              "fixed right-2 bottom-14 w-80 rounded-xl shadow-2xl z-9999 overflow-hidden",
              isTahoe && "tahoe-liquid-panel right-3 bottom-20 rounded-[32px]",
              displaySettings.glassVariant === 'clear' && isTahoe && "tahoe-clear-glass",
              isTahoe
                ? "border-white/45"
                : glassEnabled 
                ? "border-white/15" 
                : "bg-card/95 backdrop-blur-xl border border-border"
            )}
            style={glassEnabled && !isTahoe ? {
              ...glassStyles,
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
            } : {}}
            variants={isTahoe ? liquidPanel : undefined}
            initial={isTahoe ? "hidden" : { opacity: 0, y: 20, scale: 0.95 }}
            animate={isTahoe ? "visible" : { opacity: 1, y: 0, scale: 1 }}
            exit={isTahoe ? "exit" : { opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Glass highlight */}
            {glassEnabled && (
              <div 
                className="absolute inset-0 rounded-xl pointer-events-none z-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                }}
              />
            )}
            {/* Current Time Display */}
            <div className={cn(
              "px-4 py-4 border-b text-center relative z-10",
              glassEnabled ? "border-white/10" : "border-border"
            )}>
              <p className="text-4xl font-light text-foreground">
                {today.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {today.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>

            {/* Calendar Header */}
            <div className={cn(
              "flex items-center justify-between px-4 py-2 border-b relative z-10",
              glassEnabled ? "border-white/10" : "border-border"
            )}>
              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={prevMonth} aria-label="Previous month">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </span>
              </div>

              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={nextMonth} aria-label="Next month">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="p-4 relative z-10">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {daysOfWeek.map(day => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <motion.div
                    key={index}
                    className={cn(
                      "aspect-square flex items-center justify-center rounded-lg text-sm cursor-pointer transition-colors",
                      day === null && "invisible",
                      day !== null && !isToday(day) && "hover:bg-secondary text-foreground",
                      isToday(day) && "bg-primary text-primary-foreground font-bold"
                    )}
                    whileHover={day !== null ? { scale: 1.1 } : {}}
                    whileTap={day !== null ? { scale: 0.95 } : {}}
                  >
                    {day}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className={cn(
              "flex items-center justify-center px-4 py-2 border-t relative z-10",
              glassEnabled ? "border-white/10" : "border-border"
            )}>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-primary"
                onClick={goToToday}
              >
                Go to Today
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
